import React, { useState, useEffect } from "react";
import {
  Header,
  Segment,
  Loader,
  Statistic,
  Divider,
  Card,
  Grid,
  Message,
  Dropdown,
  Form,
} from "semantic-ui-react";
import { invenioConfig } from "@config";

const formatSectionLabel = (sectionValue) => {
  const labelMap = {
    Graduate_Thesis: "Graduate Thesis",
    Undergraduate_Thesis: "Undergraduate Thesis",
  };
  return labelMap[sectionValue] || sectionValue;
};

const getCsrfToken = () => {
  const cookies = document.cookie.split(";");
  const csrfCookie = cookies.find((c) => c.trim().startsWith("csrftoken="));
  if (csrfCookie) {
    const token = csrfCookie.split("=")[1];
    return token;
  }

  const metaToken = document.querySelector('meta[name="csrf-token"]')?.content;
  if (metaToken) {
    return metaToken;
  }

  return null;
};

const DocumentUsageOverview = () => {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableSections, setAvailableSections] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState(null);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [semesterOptions, setSemesterOptions] = useState([]);

  useEffect(() => {
    fetchCurrentPeriod();
    fetchAvailableSections();
    fetchAcademicYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchSemestersForYear(selectedYear);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (currentPeriod) {
      setSelectedYear(currentPeriod.academic_year);
      setSelectedSemester(currentPeriod.semester);
    }
  }, [currentPeriod]);

  useEffect(() => {
    if (selectedYear && selectedSemester && availableSections.length > 0) {
      fetchOverviewData();
    }
  }, [selectedYear, selectedSemester, availableSections]);

  const fetchCurrentPeriod = async () => {
    try {
      const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;
      const response = await fetch(`${baseURL}/document-usage/current-period`);

      if (response.ok) {
        const result = await response.json();
        if (result.status === "success" && result.data) {
          setCurrentPeriod(result.data);
        } else {
          setError(
            "No current period is set. Please configure in System Configurables."
          );
        }
      } else {
        setError(`Failed to fetch current period: ${response.status}`);
      }
    } catch (error) {
      setError("Error fetching current period: " + error.message);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;
      const response = await fetch(`${baseURL}/document-usage/years`);

      if (response.ok) {
        const result = await response.json();
        if (result.status === "success") {
          const yearOptions = result.data.map((year) => ({
            key: year.value,
            text: year.name,
            value: year.value,
          }));
          setAcademicYearOptions(yearOptions);
        } else {
          setError("Failed to fetch academic years");
        }
      } else {
        setError(`Failed to fetch academic years: ${response.status}`);
      }
    } catch (error) {
      setError("Error fetching academic years: " + error.message);
    }
  };

  const fetchSemestersForYear = async (academicYear) => {
    try {
      const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;
      const response = await fetch(
        `${baseURL}/document-usage/semesters?academic_year=${academicYear}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.status === "success") {
          const semOptions = result.data.map((sem) => ({
            key: sem.value,
            text: sem.name,
            value: sem.value,
          }));
          setSemesterOptions(semOptions);

          if (
            selectedSemester &&
            !semOptions.find((opt) => opt.value === selectedSemester)
          ) {
            setSelectedSemester(null);
          }
        } else {
          setError("Failed to fetch semesters");
          setSemesterOptions([]);
        }
      } else {
        setError(`Failed to fetch semesters: ${response.status}`);
        setSemesterOptions([]);
      }
    } catch (error) {
      setError("Error fetching semesters: " + error.message);
      setSemesterOptions([]);
    }
  };

  const fetchAvailableSections = async () => {
    try {
      const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;
      const response = await fetch(`${baseURL}/document-usage/sections`);

      if (response.ok) {
        const result = await response.json();
        if (result.status === "success") {
          setAvailableSections(result.data);
        } else {
          setError("Failed to fetch sections");
        }
      } else {
        setError(`Failed to fetch sections: ${response.status}`);
      }
    } catch (error) {
      setError("Error fetching sections: " + error.message);
    }
  };

  const fetchOverviewData = async () => {
    if (!selectedYear || !selectedSemester) return;

    try {
      setLoading(true);
      setError(null);
      const baseURL = invenioConfig.APP.REST_ENDPOINTS_BASE_URL;

      const overviewRequest = {
        academic_year: selectedYear,
        semester: selectedSemester,
        sections: availableSections.map((s) => s.value),
        transactionType: "both",
        userTypes: [],
        yearLevels: [],
        programs: [],
        departments: [],
      };

      const csrfToken = getCsrfToken();

      const headers = {
        "Content-Type": "application/json",
      };

      if (csrfToken) {
        headers["X-CSRFToken"] = csrfToken;
      }

      const response = await fetch(`${baseURL}/document-usage/report`, {
        method: "POST",
        headers: headers,
        credentials: "include",
        body: JSON.stringify(overviewRequest),
      });

      if (response.ok) {
        const result = await response.json();

        if (result.status === "success") {
          setOverviewData(result);
        } else {
          setError(
            "Failed to fetch overview data: " +
              (result.message || "Unknown error")
          );
        }
      } else {
        const errorText = await response.text();
        setError(
          `Failed to fetch overview data: ${response.status} - ${errorText}`
        );
      }
    } catch (error) {
      setError("Error fetching overview data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header as="h3" color="green">
        Document Usage Overview
      </Header>

      <Form>
        <Form.Group widths="equal">
          <Form.Field>
            <label>Academic Year</label>
            <Dropdown
              placeholder="Select Academic Year"
              fluid
              selection
              options={academicYearOptions}
              value={selectedYear}
              onChange={(e, { value }) => setSelectedYear(value)}
              loading={academicYearOptions.length === 0}
            />
          </Form.Field>
          <Form.Field>
            <label>Semester</label>
            <Dropdown
              placeholder="Select Semester"
              fluid
              selection
              options={semesterOptions}
              value={selectedSemester}
              onChange={(e, { value }) => setSelectedSemester(value)}
              loading={semesterOptions.length === 0}
              disabled={!selectedYear}
            />
          </Form.Field>
        </Form.Group>
      </Form>

      <Divider />

      {!currentPeriod || availableSections.length === 0 ? (
        <Segment>
          <Loader active inline="centered">
            Loading sections...
          </Loader>
          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}
        </Segment>
      ) : (
        <>
          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {loading ? (
            <Loader active inline="centered">
              Loading overview...
            </Loader>
          ) : overviewData ? (
            <>
              {overviewData.data?.totals?.checkouts === 0 &&
                overviewData.data?.totals?.returns === 0 && (
                  <Message info>
                    <Message.Header>No Transactions Found</Message.Header>
                    <p>{overviewData.data?.summary_message}</p>
                  </Message>
                )}

              <Statistic.Group widths="four" size="small">
                <Statistic color="blue">
                  <Statistic.Value>
                    {overviewData.data?.totals?.checkouts || 0}
                  </Statistic.Value>
                  <Statistic.Label>Total Checkouts</Statistic.Label>
                </Statistic>
                <Statistic color="purple">
                  <Statistic.Value>
                    {overviewData.data?.totals?.returns || 0}
                  </Statistic.Value>
                  <Statistic.Label>Total Returns</Statistic.Label>
                </Statistic>
                <Statistic color="green">
                  <Statistic.Value>
                    {(overviewData.data?.totals?.checkouts || 0) +
                      (overviewData.data?.totals?.returns || 0)}
                  </Statistic.Value>
                  <Statistic.Label>Total Transactions</Statistic.Label>
                </Statistic>
                <Statistic color="orange">
                  <Statistic.Value>{availableSections.length}</Statistic.Value>
                  <Statistic.Label>Library Sections</Statistic.Label>
                </Statistic>
              </Statistic.Group>

              <Divider />

              <Header as="h4">Usage by Section</Header>
              <Card.Group itemsPerRow={3}>
                {availableSections.map((section) => {
                  const sectionData = overviewData.data?.sections?.[
                    section.value
                  ] ||
                    overviewData.data?.sections?.[section.name] ||
                    overviewData.data?.sections?.[section.text] || {
                      checkouts: 0,
                      returns: 0,
                    };

                  const total =
                    (sectionData.checkouts || 0) + (sectionData.returns || 0);

                  return (
                    <Card
                      key={section.value}
                      color={total > 0 ? "yellow" : "grey"}
                    >
                      <Card.Content>
                        <Card.Header>
                          {formatSectionLabel(
                            section.name || section.text || section.value
                          )}
                        </Card.Header>
                        <Card.Meta>{total} transactions</Card.Meta>
                        <Card.Description>
                          <Grid columns={2}>
                            <Grid.Column>
                              <div>{sectionData.checkouts || 0} Checkouts</div>
                            </Grid.Column>
                            <Grid.Column>
                              <div>{sectionData.returns || 0} Returns</div>
                            </Grid.Column>
                          </Grid>
                        </Card.Description>
                      </Card.Content>
                    </Card>
                  );
                })}
              </Card.Group>
            </>
          ) : (
            <Message warning>
              <Message.Header>No Data Available</Message.Header>
              <p>No overview data found for the current period.</p>
              {currentPeriod && (
                <p style={{ marginTop: "10px", fontSize: "0.9em" }}>
                  <strong>Period:</strong> {currentPeriod.semester_name},
                  Academic Year {currentPeriod.academic_year}
                </p>
              )}
            </Message>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentUsageOverview;
